"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const MessageFramer_1 = require("./MessageFramer");
class WebRtcConnection {
    constructor(signaling, iceServers = [], useFraming = false, channelName = 'some-channel') {
        this.peers = {};
        this.onOpenCallbacks = [];
        this.onMessageCallbacks = [];
        this.onCloseCallbacks = [];
        this.useFraming = false;
        this.maxMessageSizeForFrame = 15 * 1024;
        this.messageFramer = new MessageFramer_1.MessageFramer();
        this.channelName = 'some-channel';
        this.signaling = signaling;
        this.channelName = channelName;
        this.signaling.bindInitiateOfferCallback(offerId => {
            this.initiateOffer(offerId, this.channelName);
        });
        this.signaling.bindOnIncomingDataCallback((data) => {
            this.applyRemote(data);
        });
        this.useFraming = useFraming;
        this.id = signaling.getId();
        if (iceServers.length === 0) {
            iceServers.push({ urls: 'stun:stun.l.google.com:19302' });
        }
        this.rtcConfig = {
            iceServers: iceServers,
        };
    }
    initiateOffer(id, channelName = 'some-channel') {
        this.peers[id] = {
            candidateCache: [],
        };
        const pc = new RTCPeerConnection(this.rtcConfig);
        this.initConnection(pc, id);
        this.peers[id].connection = pc;
        const channel = pc.createDataChannel(channelName);
        this.peers[id].channel = channel;
        this.bindChannelEvents(id, channel);
        pc.onnegotiationneeded = () => __awaiter(this, void 0, void 0, function* () {
            yield pc.setLocalDescription(yield pc.createOffer());
        });
    }
    sendMessage(message) {
        this.splitMessageToFrames(message).then((preparedMessages) => {
            for (const peer in this.peers) {
                const channel = this.peers[peer].channel;
                if (channel !== undefined && channel.readyState === 'open') {
                    for (const message of preparedMessages) {
                        channel.send(message);
                    }
                }
            }
        });
    }
    close() {
        for (const peer in this.peers) {
            const channel = this.peers[peer].channel;
            if (channel !== undefined) {
                channel.close();
            }
            const connection = this.peers[peer].connection;
            if (connection !== undefined) {
                connection.close();
            }
            if (this.peers[peer].wasSuccess) {
                for (const callback of this.onCloseCallbacks) {
                    callback();
                }
            }
        }
    }
    addOnOpenCallback(callback) {
        this.onOpenCallbacks.push(callback);
    }
    addOnMessageCallback(callback) {
        this.onMessageCallbacks.push(callback);
    }
    addOnCloseCallback(callback) {
        this.onCloseCallbacks.push(callback);
    }
    applyRemote(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (data.sdp.type === 'answer') {
                yield this.remoteAnswerReceived(data.id, data.sdp);
            }
            else {
                yield this.remoteOfferReceived(data.id, data.sdp);
            }
            for (let i = 0; i < data.ice.length; i++) {
                this.remoteCandidateReceived(data.id, data.ice[i]);
            }
        });
    }
    initConnection(pc, id) {
        pc.onicecandidate = (event) => {
            if (!pc.localDescription || !this.peers[id]) {
                return;
            }
            if (event.candidate) {
                this.peers[id].candidateCache.push(event.candidate);
            }
            else {
                const data = {
                    id: this.id,
                    sdp: pc.localDescription,
                    ice: this.peers[id].candidateCache,
                };
                this.signaling.send(id, data);
            }
        };
        pc.oniceconnectionstatechange = () => {
            switch (pc.iceConnectionState) {
                case 'failed':
                    // Firefox
                    if (this.peers[id].wasSuccess) {
                        for (const callback of this.onCloseCallbacks) {
                            callback();
                        }
                    }
                    this.signaling.onWebRtcConnectionError(id);
                    delete this.peers[id];
                    break;
            }
        };
        pc.onconnectionstatechange = () => {
            switch (pc.connectionState) {
                case 'disconnected':
                    // Chrome
                    if (this.peers[id].wasSuccess) {
                        for (const callback of this.onCloseCallbacks) {
                            callback();
                        }
                    }
                    this.signaling.onWebRtcConnectionError(id);
                    delete this.peers[id];
                    break;
            }
        };
    }
    bindChannelEvents(id, channel) {
        channel.onopen = () => {
            this.peers[id].wasSuccess = true;
            for (const callback of this.onOpenCallbacks) {
                callback();
            }
        };
        channel.onclose = () => {
            // Nothing to do
        };
        channel.onmessage = (e) => {
            this.prepareIncomingMessage(e.data).then((message) => {
                if (typeof message === 'undefined') {
                    return;
                }
                for (const callback of this.onMessageCallbacks) {
                    callback(message, id);
                }
            });
        };
    }
    remoteOfferReceived(remoteId, sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            this.createConnection(remoteId);
            const pc = this.peers[remoteId].connection;
            if (!pc) {
                return;
            }
            yield pc.setRemoteDescription(sdp);
            const answer = yield pc.createAnswer();
            yield pc.setLocalDescription(answer);
        });
    }
    remoteAnswerReceived(id, sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            this.createConnection(id);
            const pc = this.peers[id].connection;
            if (!pc) {
                return;
            }
            yield pc.setRemoteDescription(sdp);
        });
    }
    remoteCandidateReceived(id, ice) {
        return __awaiter(this, void 0, void 0, function* () {
            const pc = this.peers[id].connection;
            if (!pc) {
                return;
            }
            yield pc.addIceCandidate(ice);
        });
    }
    createConnection(id) {
        if (this.peers[id] === undefined) {
            this.peers[id] = {
                candidateCache: [],
            };
            const pc = new RTCPeerConnection(this.rtcConfig);
            this.initConnection(pc, id);
            this.peers[id].connection = pc;
            pc.ondatachannel = (e) => {
                this.peers[id].channel = e.channel;
                this.bindChannelEvents(id, e.channel);
            };
        }
    }
    splitMessageToFrames(message) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.useFraming) {
                return [message];
            }
            return yield this.messageFramer.splitMessageToFrames(message, this.maxMessageSizeForFrame);
        });
    }
    prepareIncomingMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.useFraming) {
                return message;
            }
            return yield this.messageFramer.prepareIncomingMessage(message);
        });
    }
}
exports.WebRtcConnection = WebRtcConnection;
